import {
  Popover,
  Stack,
  Box,
  Text,
  styled,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useState } from 'react';

import { cleanOpinionType } from 'routes/Dashboard/CourtCases/v2/utils';

import { StyledButton } from '../MessageTeaser';

const StyledStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(2),
}));

type OpinionOption = {
  id: number | string;
  type: string;
  author_str?: string;
  html_with_citations?: string;
};

type OpinionSelectProps = {
  opinions: OpinionOption[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

export const OpinionSelect = ({
  opinions,
  selectedIndex,
  onSelect,
}: OpinionSelectProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (index: number) => {
    onSelect(index);
    handleClose();
  };

  if (opinions.length === 0) {
    return null;
  }

  const selectedOpinion = opinions[selectedIndex];
  const opinionType = cleanOpinionType(selectedOpinion?.type || 'Unknown');
  const authorText =
    selectedOpinion &&
    'author_str' in selectedOpinion &&
    selectedOpinion.author_str
      ? ` by ${selectedOpinion.author_str}`
      : '';
  const displayText = `${opinionType}${authorText}`;

  return (
    <>
      <StyledButton
        variant="outlined"
        size="small"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          width: isMobile ? '100%' : '315px',
          justifyContent: 'space-between',
          margin: '20px 0',
        }}
      >
        {displayText}
        <KeyboardArrowDown sx={{ ml: 0.5 }} />
      </StyledButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: theme.spacing(2),
            width: '315px',
          },
        }}
      >
        <StyledStack spacing={1}>
          {opinions.map((opinion, index) => {
            const opType = cleanOpinionType(opinion.type || 'Unknown');
            const authText =
              'author_str' in opinion && opinion.author_str
                ? ` by ${opinion.author_str}`
                : '';
            const opDisplayText = `${opType}${authText}`;
            const isSelected = index === selectedIndex;

            return (
              <Box
                key={opinion.id || index}
                onClick={() => handleSelect(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(index);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Select ${opDisplayText}`}
                sx={{
                  cursor: 'pointer',
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: isSelected
                    ? theme.palette.action.selected
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Text
                  variant="body1"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {opDisplayText}
                </Text>
              </Box>
            );
          })}
        </StyledStack>
      </Popover>
    </>
  );
};
